import moment from "moment";

// compute quarterly values from a cprRecommendation (scenario)
export const generateChartData = (
  portfolio,
  annualWithdrawal = 0,
  seekTotalWithdrawal = false
) => {
  if (
    !portfolio ||
    !portfolio.cprRecommendation ||
    !portfolio.cprRecommendation.chartData ||
    !portfolio.metadata
  ) {
    return null;
  }

  const { cprRecommendation } = portfolio;
  const initialInvestment = portfolio.metadata.initialInvestment || 0;
  const inflation = portfolio.metadata.advisorConfig.inflationRate || 0;

  const cyGrowth = cprRecommendation.chartData["CprQtrReturn"];
  const benchmarkGrowth = cprRecommendation.chartData["BenchmarkQtrReturn"];
  const startDate = cprRecommendation.chartData["startDate"];

  let totalWithdrawal = { Cy: 0, Benchmark: 0 };

  const incomeBreakdown = [];

  const applyQtr = (portfolioType, prevQtr, growth, qtrIdx) => {
    let year = Math.floor(qtrIdx / 4);
    let withdrawal = (annualWithdrawal * Math.pow(1 + inflation, year)) / 4;
    let v = prevQtr * (1 + Number(growth)) - withdrawal;
    if (!incomeBreakdown[qtrIdx]) {
      incomeBreakdown.push({ Cy: 0, Benchmark: 0 });
    }
    if (prevQtr * (1 + Number(growth)) <= withdrawal) {
      totalWithdrawal[portfolioType] =
        totalWithdrawal[portfolioType] + prevQtr * (1 + Number(growth));
      incomeBreakdown[qtrIdx][portfolioType] = prevQtr * (1 + Number(growth));
    } else {
      totalWithdrawal[portfolioType] =
        totalWithdrawal[portfolioType] + withdrawal;
      incomeBreakdown[qtrIdx][portfolioType] = withdrawal;
    }
    return v < 0 ? 0 : v;
  };

  // initVal is the index 0, dated 1999-12-31
  const initVal = {
    date: moment(startDate)
      .subtract(3, "months")
      .endOf("month")
      .format("YYYY-MM-DD"),
    Cy: initialInvestment,
    Benchmark: initialInvestment,
  };

  const chartData = cyGrowth.reduce(
    (qtrVals, cyQtr, index) => {
      const benchmarkQtr = benchmarkGrowth[index];
      const curVal = qtrVals[qtrVals.length - 1];

      const nextVal = {
        date: moment(curVal["date"])
          .add(3, "months")
          .endOf("month")
          .format("YYYY-MM-DD"),
        Cy: applyQtr("Cy", curVal["Cy"], cyQtr, index),
        Benchmark: applyQtr(
          "Benchmark",
          curVal["Benchmark"],
          benchmarkQtr,
          index
        ),
      };
      return qtrVals.concat([nextVal]);
    },
    [initVal]
  );

  chartData.shift(); // to remove index 0 of dated 1999-12-31

  if (seekTotalWithdrawal) {
    const incomeBreakdownPerQuarter = incomeBreakdown.map((item, index) => ({
      ...item,
      date: moment(startDate)
        .add(3 * index, "months")
        .endOf("month")
        .format("YYYY-MM-DD"),
    }));
    let incomeBreakdownPerYear = [];
    incomeBreakdownPerQuarter.forEach((quarter) => {
      const year = moment(quarter.date).year();
      const idx = incomeBreakdownPerYear.findIndex((v) => v.year === year);
      if (idx >= 0) {
        incomeBreakdownPerYear[idx]["Cy"] =
          incomeBreakdownPerYear[idx]["Cy"] + quarter["Cy"];
        incomeBreakdownPerYear[idx]["Benchmark"] =
          incomeBreakdownPerYear[idx]["Benchmark"] + quarter["Benchmark"];
      } else {
        incomeBreakdownPerYear.push({
          year,
          Cy: quarter["Cy"],
          Benchmark: quarter["Benchmark"],
        });
      }
    });

    return {
      inflation,
      totalWithdrawal,
      incomeBreakdown: incomeBreakdownPerYear,
    };
  } else {
    return chartData;
  }
};

export const getRiskPeriod = (value) => {
  let riskPeriod = {
    "worst.calendar.year": "Worst Calendar Year",
    "worst.4q": "Worst Four Quarters",
    mdd: "Worst Drawdown",
  };

  return riskPeriod[value];
};

export const getRiskPeriodLossTolerance = (value) => {
  let riskPeriod = {
    "worst.calendar.year": "WorstCalendarYearReturn",
    "worst.4q": "Worst4QtrReturn",
    mdd: "MaxDrawdown",
  };

  return riskPeriod[value];
};


export const generateScenarioString = ({
    includeTheoretical,
    riskAverse,
    managerType,
    riskPeriod,
    rror,
    riskTolerance,
    solveFor,
    singleStrategy,
    cprScenarioStringPrefix = ''
}) => {
  if (['WBI Portfolios', 'WBI Envestnet', 'Other Models'].includes(managerType)) {
    return singleStrategy
  }

  if (solveFor === 'ret') {
    return `${cprScenarioStringPrefix}${includeTheoretical ? 'hypothetical.' : ''}${managerType}.${riskAverse ? 'ra.' : ''}${solveFor}.${(Math.round(rror * 100) / 100)}`;
  } else {
    return `${cprScenarioStringPrefix}${includeTheoretical ? 'hypothetical.' : ''}${managerType}.${riskAverse ? 'ra.' : ''}${riskPeriod}.${parseFloat(riskTolerance).toFixed(2).split('.')[1]}`;
  }
};

export const convertPerformanceType = (managerDetail) => {
  let performanceType;
  switch (managerDetail['Type']) {
    case 'Exchange-Traded Fund': {
      performanceType = 'ETF';
      break;
    }
    case 'Open-End Fund': {
      performanceType = 'Mutual Fund';
      break;
    }
    case 'Index': {
      performanceType = 'Index / ETF Blend ***';
      break;
    }
    case 'Back-Tested Model': {
      /**
       * IF
       *  “Investment Type” = “Back-Tested Model” AND
       *  “Implementation Type” = “Separate Account” AND
       *  “Implementation Inception Date” <= at least one month before End of Previous Year
       * THEN Performance Type displays “Backtest/ Separate Account Blend”.
       */
      performanceType = 'Backtest / Separate Account Blend ***';
      /**
       * IF
       *  “Investment Type” = “Back-Tested Model” AND
       *  “Implementation Inception Date” > at least one month before End of Previous Year
       * THEN it displays “Backtest”
       */
      if (
        moment(managerDetail['ImplementationInceptionDate']).isAfter(
          END_OF_CPR_YEAR
            .clone()
            .startOf('month')
            .subtract(1, 'month')
            .endOf('month')
        )
      ) {
        performanceType = 'Backtest';
      }
      break;
    }
    default: {
      performanceType = managerDetail['Type'];
    }
  }
  return performanceType
}

export const END_OF_CPR_YEAR = moment('2025-12-31');


// WEBPACK FOOTER //
// ./src/utilities/cpr.js